import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { 
  Search, 
  Plus, 
  Filter, 
  Mail, 
  Phone, 
  Calendar, 
  User, 
  Edit, 
  Trash2, 
  X,
  Check,
  Users,
  Grid,
  List,
  UserPlus,
  MapPin,
  ChevronDown,
  ChevronUp,
  Pencil,
  Clock,
  RefreshCw,
  Heart,
  Baby,
  Crown,
  ArrowRight,
  MoreVertical,
  Star,
  MessageSquare,
  Hash,
  UserMinus,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  UserCheck,
  UserX,
  Eye,
  Download,
  Share2,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Target,
  Activity,
  TrendingUp,
  BarChart3,
  PieChart,
  LineChart,
  Settings,
  Bell,
  MessageCircle,
  Send,
  Reply,
  Forward,
  Copy,
  Bookmark,
  Flag,
  Tag,
  Hash as HashIcon,
  AtSign,
  Globe,
  Home,
  Building,
  GraduationCap,
  Briefcase,
  Coffee,
  Gift,
  Cake,
  Music,
  Camera,
  Video,
  Mic,
  Headphones,
  Speaker,
  Volume2,
  VolumeX,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  RotateCcw,
  RotateCw,
  Shuffle,
  Repeat,
  Repeat1,
  Shuffle as ShuffleIcon,
  Repeat as RepeatIcon,
  Repeat1 as Repeat1Icon,
  Shuffle as ShuffleIcon2,
  Repeat as RepeatIcon2,
  Repeat1 as Repeat1Icon2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { PermissionGuard, PermissionButton } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@/lib/permissions.jsx';
import { getMembers, addMember, updateMember, deleteMember, getGroups, getCurrentUserOrganizationId } from '@/lib/data';
import { familyService } from '@/lib/familyService';
import { useToast } from '@/components/ui/use-toast';
import MemberForm from '@/components/members/MemberForm';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/lib/authContext';
import { formatName, getInitials } from '@/lib/utils/formatters';

export function MobileMembers() {
  console.log('MobileMembers: Component rendered');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [sortField, setSortField] = useState('lastname');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullToRefreshY, setPullToRefreshY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFamilyDialogOpen, setIsFamilyDialogOpen] = useState(false);
  const [isFamilyDetailsDialogOpen, setIsFamilyDetailsDialogOpen] = useState(false);
  
  // Selected items
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedFamilyForDetails, setSelectedFamilyForDetails] = useState(null);
  const [isEditFamilyDialogOpen, setIsEditFamilyDialogOpen] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [newFamily, setNewFamily] = useState({
    family_name: '',
    primary_contact_id: ''
  });
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [isDeleteGroupDialogOpen, setIsDeleteGroupDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isViewGroupOpen, setIsViewGroupOpen] = useState(false);
  const [isAddMemberToGroupOpen, setIsAddMemberToGroupOpen] = useState(false);
  const [isEditGroupOpen, setIsEditGroupOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  // Load members
  const loadMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const membersData = await getMembers();
      setMembers(membersData || []);
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: "Error",
        description: "Failed to load members",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Load families
  const loadFamilies = useCallback(async () => {
    try {
      const familiesData = await familyService.getFamilies();
      setFamilies(familiesData || []);
    } catch (error) {
      console.error('Error loading families:', error);
    }
  }, []);

  // Load groups
  const loadGroups = useCallback(async () => {
    try {
      const groupsData = await getGroups();
      setGroups(groupsData || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  }, []);

  // Filter members based on search and filters
  const filteredMembers = useMemo(() => {
    let filtered = members;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(member =>
        `${member.firstname} ${member.lastname}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.phone?.includes(searchQuery)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(member => member.status === statusFilter);
    }

    // Age filter
    if (ageFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(member => {
        if (!member.birthdate) return ageFilter === 'unknown';
        const age = now.getFullYear() - new Date(member.birthdate).getFullYear();
        if (ageFilter === 'children') return age < 18;
        if (ageFilter === 'adults') return age >= 18 && age < 65;
        if (ageFilter === 'seniors') return age >= 65;
        return true;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (sortField === 'name') {
        aValue = `${a.firstname} ${a.lastname}`;
        bValue = `${b.firstname} ${b.lastname}`;
      }
      
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [members, searchQuery, statusFilter, ageFilter, sortField, sortDirection]);

  // Pull to refresh functionality
  const handleTouchStart = useCallback((e) => {
    if (window.scrollY === 0) {
      setPullToRefreshY(e.touches[0].clientY);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (window.scrollY === 0 && e.touches[0].clientY > pullToRefreshY) {
      const deltaY = e.touches[0].clientY - pullToRefreshY;
      if (deltaY > 0) {
        setIsPulling(true);
        setPullToRefreshY(e.touches[0].clientY);
      }
    }
  }, [pullToRefreshY]);

  const handleTouchEnd = useCallback(async () => {
    if (isPulling && window.scrollY === 0) {
      setRefreshing(true);
      await loadMembers();
      setRefreshing(false);
    }
    setIsPulling(false);
    setPullToRefreshY(0);
  }, [isPulling, loadMembers]);

  useEffect(() => {
    loadMembers();
    loadFamilies();
    loadGroups();
  }, [loadMembers, loadFamilies, loadGroups]);

  // Add touch event listeners
  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const handleAddMember = async (memberData) => {
    try {
      await addMember(memberData);
      toast({
        title: "Success",
        description: "Member added successfully",
      });
      setIsAddDialogOpen(false);
      loadMembers();
    } catch (error) {
      console.error('Error adding member:', error);
      toast({
        title: "Error",
        description: "Failed to add member",
        variant: "destructive"
      });
    }
  };

  const handleUpdateMember = async (memberData) => {
    try {
      await updateMember(selectedMember.id, memberData);
      toast({
        title: "Success",
        description: "Member updated successfully",
      });
      setIsEditDialogOpen(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error) {
      console.error('Error updating member:', error);
      toast({
        title: "Error",
        description: "Failed to update member",
        variant: "destructive"
      });
    }
  };

  const handleDeleteMember = async () => {
    try {
      await deleteMember(selectedMember.id);
      toast({
        title: "Success",
        description: "Member deleted successfully",
      });
      setIsDeleteDialogOpen(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast({
        title: "Error",
        description: "Failed to delete member",
        variant: "destructive"
      });
    }
  };

  const getMemberStatusColor = (status) => {
    const colors = {
      'active': 'bg-green-500',
      'inactive': 'bg-gray-500',
      'pending': 'bg-yellow-500',
      'visitor': 'bg-blue-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getMemberStatusIcon = (status) => {
    const icons = {
      'active': CheckCircle,
      'inactive': XCircle,
      'pending': Clock,
      'visitor': User
    };
    return icons[status] || User;
  };

  const getAgeGroup = (birthdate) => {
    if (!birthdate) return 'unknown';
    const age = new Date().getFullYear() - new Date(birthdate).getFullYear();
    if (age < 18) return 'children';
    if (age >= 18 && age < 65) return 'adults';
    return 'seniors';
  };

  const getAgeGroupIcon = (ageGroup) => {
    const icons = {
      'children': Baby,
      'adults': User,
      'seniors': Crown,
      'unknown': User
    };
    return icons[ageGroup] || User;
  };

  const getAgeGroupColor = (ageGroup) => {
    const colors = {
      'children': 'text-pink-500',
      'adults': 'text-blue-500',
      'seniors': 'text-yellow-500',
      'unknown': 'text-gray-500'
    };
    return colors[ageGroup] || 'text-gray-500';
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <div className="h-8 bg-muted rounded w-32 animate-pulse" />
          <div className="h-4 bg-muted rounded w-48 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-32 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Pull to refresh indicator */}
      <AnimatePresence>
        {refreshing && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-16 left-0 right-0 z-50 bg-primary text-primary-foreground py-2 text-center"
          >
            <RefreshCw className="h-4 w-4 animate-spin inline mr-2" />
            Refreshing...
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">People</h1>
            <p className="text-muted-foreground">
              {filteredMembers.length} people
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
            </Button>
            <PermissionButton permission={PERMISSIONS.MEMBERS_EDIT}>
              <Button
                size="sm"
                onClick={() => setIsAddDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </PermissionButton>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search people..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3 p-4 bg-muted/50 rounded-lg"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={statusFilter}
                    onValueChange={setStatusFilter}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="visitor">Visitor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Age Group</Label>
                  <Select
                    value={ageFilter}
                    onValueChange={setAgeFilter}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ages</SelectItem>
                      <SelectItem value="children">Children</SelectItem>
                      <SelectItem value="adults">Adults</SelectItem>
                      <SelectItem value="seniors">Seniors</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Sort By</Label>
                <Select
                  value={sortField}
                  onValueChange={setSortField}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lastname">Last Name</SelectItem>
                    <SelectItem value="firstname">First Name</SelectItem>
                    <SelectItem value="name">Full Name</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="created_at">Date Added</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Toggle */}
        <div className="flex items-center justify-between">
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="grid">Grid</TabsTrigger>
              <TabsTrigger value="list">List</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Members Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-2 gap-4">
            {filteredMembers.map((member) => {
              const ageGroup = getAgeGroup(member.birthdate);
              const StatusIcon = getMemberStatusIcon(member.status);
              const AgeGroupIcon = getAgeGroupIcon(ageGroup);
              
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/members/${member.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div className="relative">
                          <Avatar className="h-16 w-16">
                            <AvatarImage 
                              src={member.image_url} 
                              alt={formatName(member.firstname, member.lastname)}
                            />
                            <AvatarFallback className="text-lg">
                              {getInitials(member.firstname, member.lastname)}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`absolute -bottom-1 -right-1 p-1 rounded-full ${getMemberStatusColor(member.status)}`}>
                            <StatusIcon className="h-3 w-3 text-white" />
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <h3 className="font-medium text-sm text-foreground truncate w-full">
                            {formatName(member.firstname, member.lastname)}
                          </h3>
                          <p className="text-xs text-muted-foreground truncate w-full">
                            {member.email}
                          </p>
                          {member.phone && (
                            <p className="text-xs text-muted-foreground truncate w-full">
                              📞 {member.phone}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {ageGroup !== 'unknown' && (
                            <Badge variant="secondary" className="text-xs">
                              <AgeGroupIcon className={`h-3 w-3 mr-1 ${getAgeGroupColor(ageGroup)}`} />
                              {ageGroup}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {member.status}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Members List View */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {filteredMembers.map((member) => {
              const ageGroup = getAgeGroup(member.birthdate);
              const StatusIcon = getMemberStatusIcon(member.status);
              const AgeGroupIcon = getAgeGroupIcon(ageGroup);
              
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/members/${member.id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <Avatar className="h-12 w-12">
                            <AvatarImage 
                              src={member.image_url} 
                              alt={formatName(member.firstname, member.lastname)}
                            />
                            <AvatarFallback>
                              {getInitials(member.firstname, member.lastname)}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`absolute -bottom-1 -right-1 p-1 rounded-full ${getMemberStatusColor(member.status)}`}>
                            <StatusIcon className="h-3 w-3 text-white" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm text-foreground truncate">
                            {formatName(member.firstname, member.lastname)}
                          </h3>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.email}
                          </p>
                          {member.phone && (
                            <p className="text-xs text-muted-foreground truncate">
                              📞 {member.phone}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          {ageGroup !== 'unknown' && (
                            <Badge variant="secondary" className="text-xs">
                              <AgeGroupIcon className={`h-3 w-3 mr-1 ${getAgeGroupColor(ageGroup)}`} />
                              {ageGroup}
                            </Badge>
                          )}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {filteredMembers.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {searchQuery ? 'No members found matching your search' : 'No members found'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Member Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
          </DialogHeader>
          <MemberForm
            onSubmit={handleAddMember}
            onCancel={() => setIsAddDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <MemberForm
              member={selectedMember}
              onSubmit={handleUpdateMember}
              onCancel={() => {
                setIsEditDialogOpen(false);
                setSelectedMember(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Member Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle>Delete Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedMember?.firstname} {selectedMember?.lastname}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMember}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}